/**
 * Load extension UIs from an Engine Gateway's HTTP endpoints.
 *
 * Flow (mirrors `loadGatewayPlugins`; see docs/03-Implementation/06-Extensions/system.md):
 *   1. Fetch /api/extensions/ui-manifest.json → list of extensions with UI bundles
 *   2. For each extension, fetch source text from /api/extensions/:id/ui.js
 *   3. Load via importExtensionUIFromSource (Blob URL → import) to avoid
 *      cross-origin import() failures in Tauri WKWebView
 *   4. Register each ExtensionUI in the extension-ui-registry
 */

import { useLogStore } from "../store/log-store";
import { frontendLog } from "../utils/frontend-logger";
import { importExtensionUIFromSource } from "./extension-loader";
import { extensionUIRegistry, registerExtensionUI } from "./extension-ui-registry";

export interface ExtensionManifestEntry {
    id: string;
    version: string;
    title?: string;
    icon?: string;
    hasUI: boolean;
    hasCSS?: boolean;
}

export interface ExtensionUIManifestResponse {
    extensions: ExtensionManifestEntry[];
}

export interface GatewayExtensionOptions {
    /**
     * Custom fetch function for environments where standard fetch() doesn't work
     * (e.g. Tauri WKWebView on iOS needs tauriFetch to bypass CORS/ATS).
     * Defaults to globalThis.fetch.
     */
    fetchFn?: typeof globalThis.fetch;
    /** Whether to warn when the Gateway manifest contains no extensions. */
    warnIfEmpty?: boolean;
}

/**
 * Fetch the extension UI manifest from an Engine Gateway.
 *
 * @returns The manifest response, or null if the fetch failed.
 */
export async function fetchExtensionManifest(
    gatewayUrl: string,
    options?: GatewayExtensionOptions,
): Promise<ExtensionUIManifestResponse | null> {
    const log = useLogStore.getState();
    const baseUrl = gatewayUrl.replace(/\/+$/, "");
    const doFetch = options?.fetchFn ?? globalThis.fetch.bind(globalThis);

    try {
        const res = await doFetch(`${baseUrl}/api/extensions/ui-manifest.json`);
        if (!res.ok) {
            throw new Error(`HTTP ${res.status}: ${res.statusText}`);
        }
        return (await res.json()) as ExtensionUIManifestResponse;
    } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        log.error(`Failed to fetch extension manifest: ${msg}`, { source: "system" });
        return null;
    }
}

/**
 * Load all extension UI bundles from an Engine Gateway.
 *
 * @returns loaded/failed extension ids.
 */
export async function loadGatewayExtensions(
    gatewayUrl: string,
    options?: GatewayExtensionOptions,
): Promise<{ loaded: string[]; failed: string[] }> {
    const log = useLogStore.getState();
    const baseUrl = gatewayUrl.replace(/\/+$/, "");

    const manifest = await fetchExtensionManifest(gatewayUrl, options);
    if (!manifest) {
        return { loaded: [], failed: [] };
    }

    const uiExtensions = manifest.extensions.filter((e) => e.hasUI);
    if (uiExtensions.length === 0) {
        if (options?.warnIfEmpty !== false) {
            log.warn("No extensions with UI found on Gateway", { source: "system" });
        }
        return { loaded: [], failed: [] };
    }

    log.info(
        `Found ${uiExtensions.length} extensions with UI: ${uiExtensions.map((e) => e.id).join(", ")}`,
        { source: "system" },
    );

    const doFetch = options?.fetchFn ?? globalThis.fetch.bind(globalThis);
    const loaded: string[] = [];
    const failed: string[] = [];

    const results = await Promise.allSettled(
        uiExtensions.map(async (e) => {
            const url = `${baseUrl}/api/extensions/${e.id}/ui.js`;
            const res = await doFetch(url);
            if (!res.ok) {
                throw new Error(`HTTP ${res.status} fetching ${url}`);
            }
            const source = await res.text();
            const ui = await importExtensionUIFromSource(source, e.id);
            registerExtensionUI(ui);

            if (e.hasCSS) {
                try {
                    const cssRes = await doFetch(`${baseUrl}/api/extensions/${e.id}/ui.css`);
                    if (cssRes.ok) {
                        const cssText = await cssRes.text();
                        injectExtensionCss(e.id, cssText);
                    }
                } catch {
                    // CSS is optional — ignore fetch failures.
                }
            }

            return ui.manifest.id;
        }),
    );

    for (const [i, result] of results.entries()) {
        if (result.status === "fulfilled") {
            loaded.push(result.value);
        } else {
            failed.push(uiExtensions[i].id);
            frontendLog.error(
                `[extension-loader] Failed to load gateway extension ${uiExtensions[i].id}`,
                result.reason,
                { source: "extension" },
            );
        }
    }

    if (failed.length > 0) {
        log.warn(`Failed to load ${failed.length} extensions: ${failed.join(", ")}`, {
            source: "system",
        });
    }

    log.info(
        `Loaded ${extensionUIRegistry.size} gateway extensions: ${extensionUIRegistry.ids().join(", ")}`,
        { source: "system" },
    );

    return { loaded, failed };
}

function injectExtensionCss(id: string, cssText: string): void {
    if (!cssText.trim() || typeof document === "undefined") return;
    const styleId = `magic-md-extension-css-${id}`;
    if (document.getElementById(styleId)) return;
    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = cssText;
    document.head.appendChild(style);
}
